import React, { useState, memo, useEffect } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useLocation, useNavigate } from "react-router-dom";

import logo from "../../assests/images/logo.png";
import companyName from "../../assests/images/VerifiedNav.png";
import OptimizedImage from "../OptimizedImage";

const pageRoutes = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Who We Are", path: "/who-we-are" },
  { label: "Contact Us", path: "/contact" },
];

// Styled Tab
const StyledTab = ({ theme, ...props }) => (
  <Tab
    {...props}
    sx={{
      color: "#000",
      textTransform: "none",
      fontWeight: "normal",
      opacity: 1,
      "&.Mui-selected": {
        fontWeight: "bold",
        color: `${theme.palette.secondary.main} !important`,
        opacity: 1,
      },
      "&:hover": {
        color: `${theme.palette.secondary.main} !important`,
        opacity: 1,
      },
    }}
  />
);

function Nav() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const isTablet = useMediaQuery("(max-width:1349px)"); // tablet and below
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // mobile only

  const [anchorEl, setAnchorEl] = useState(null);

  // Close mobile menu on route change
  useEffect(() => {
    setAnchorEl(null);
  }, [location.pathname]);

  const currentTabIndex = pageRoutes.findIndex((page) =>
    page.path === "/" ? location.pathname === "/" : location.pathname.startsWith(page.path)
  );

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = (path) => {
    setAnchorEl(null);
    if (path) navigate(path);
  };

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 1200,
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
        }}
      >
        <OptimizedImage
          src="https://res.cloudinary.com/dd2f8xsjr/image/upload/f_auto,q_auto/bgimage_m91gpx"
          alt="Header Background"
          widths={[480, 768, 1200, 1920]}
          sizes="100vw"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.3)",
            zIndex: 1,
          }}
        />
      </Box>

      {/* Left Logo: only tablet & desktop */}
      {!isMobile && (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
          <img
            src={logo}
            alt="Logo"
            loading="eager"
            fetchpriority="high"
            style={{
              height: "auto",
              maxHeight: "50px",
              width: "auto",
              cursor: "pointer",
              position: "relative",
              zIndex: 2,
            }}
            onClick={() => navigate("/")}
          />
        </Box>
      )}

      {/* Center: companyName (all sizes) */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
        }}
      >
        <img
          src={companyName}
          alt="Company Name"
          style={{
            maxHeight: isMobile ? "50px" : "80px",
            width: "auto",
            objectFit: "contain",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        />
      </Box>

      {/* Right: Hamburger / Tabs */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
        {isTablet ? (
          <>
            <IconButton
              onClick={handleMenuClick}
              sx={{ position: "relative", zIndex: 2 }}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>

            <Menu
              key={location.pathname}
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => handleMenuClose()}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {pageRoutes.map((page) => (
                <MenuItem
                  key={page.label}
                  onClick={() => handleMenuClose(page.path)}
                >
                  {page.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              zIndex: 2,
            }}
          >
            {pageRoutes.map((page, index) => (
              <React.Fragment key={page.label}>
                <StyledTab
                  theme={theme}
                  label={page.label}
                  onClick={() => navigate(page.path)}
                  selected={currentTabIndex === index}
                />
                {index < pageRoutes.length - 1 && (
                  <Box component="span" sx={{ color: "#000", mx: 1 }}>
                    |
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default memo(Nav);